import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { COMPANY, NAV_LINKS } from "../data/content";

export const scrollToHash = (hash) => {
    const el = document.querySelector(hash);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    const go = (hash) => {
        setOpen(false);
        if (location.pathname !== "/") {
            navigate("/" + hash);
        } else {
            window.history.replaceState(null, "", hash);
            scrollToHash(hash);
        }
    };

    return (
        <header
            data-testid="navbar"
            className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 ${
                scrolled || open
                    ? "border-b border-slate-200 bg-white/95 shadow-[0_2px_20px_-8px_rgba(15,23,42,0.15)] backdrop-blur-md"
                    : "border-b border-transparent bg-white/80 backdrop-blur-sm"
            }`}
        >
            <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 sm:px-8">
                <button
                    type="button"
                    data-testid="navbar-brand-logo"
                    onClick={() => go("#home")}
                    className="flex items-center gap-3"
                    aria-label="PISI LIMITED home"
                >
                    <span className="flex h-10 w-10 items-center justify-center bg-navy-900 font-heading text-lg font-bold text-gold">
                        P
                    </span>
                    <span className="text-left leading-tight">
                        <span className="block font-heading text-[15px] font-bold tracking-tight text-navy-900">
                            {COMPANY.name}
                        </span>
                        <span className="block text-[10px] font-medium uppercase tracking-[0.22em] text-slate-500">
                            {COMPANY.tagline}
                        </span>
                    </span>
                </button>

                <nav className="hidden items-center gap-8 lg:flex" aria-label="Main navigation">
                    {NAV_LINKS.map((link) => (
                        <button
                            key={link.hash}
                            type="button"
                            data-testid={link.testId}
                            onClick={() => go(link.hash)}
                            className="text-sm font-medium text-slate-600 transition-colors duration-200 hover:text-navy-900"
                        >
                            {link.label}
                        </button>
                    ))}
                    <button
                        type="button"
                        data-testid="nav-cta-enquire-now"
                        onClick={() => go("#contact")}
                        className="group inline-flex items-center gap-2 bg-navy-900 px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-navy-800"
                    >
                        Enquire Now
                        <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                    </button>
                </nav>

                <button
                    type="button"
                    data-testid="nav-mobile-menu-toggle"
                    onClick={() => setOpen((v) => !v)}
                    className="flex h-10 w-10 items-center justify-center text-navy-900 lg:hidden"
                    aria-label={open ? "Close menu" : "Open menu"}
                    aria-expanded={open}
                >
                    {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </button>
            </div>

            {open && (
                <div
                    data-testid="nav-mobile-menu"
                    className="border-t border-slate-100 bg-white px-5 pb-6 pt-2 lg:hidden"
                >
                    {NAV_LINKS.map((link) => (
                        <button
                            key={link.hash}
                            type="button"
                            data-testid={`mobile-${link.testId}`}
                            onClick={() => go(link.hash)}
                            className="block w-full border-b border-slate-100 py-3.5 text-left text-[15px] font-medium text-slate-700 transition-colors hover:text-navy-900"
                        >
                            {link.label}
                        </button>
                    ))}
                    <button
                        type="button"
                        data-testid="mobile-nav-cta-enquire-now"
                        onClick={() => go("#contact")}
                        className="mt-4 flex w-full items-center justify-center gap-2 bg-navy-900 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-navy-800"
                    >
                        Enquire Now
                        <ArrowRight className="h-4 w-4" />
                    </button>
                </div>
            )}
        </header>
    );
};
